import React, { useState } from 'react';
import { ApiThumbnailSize, toApiThumbnailUrl } from '../utils/apiThumbnail';

// =====================================================================
// <ThumbnailImage>: rendert für kleine Karten/Avatare zuerst das
// Backend-Thumbnail (/api/file/thumbnails/…) und fällt bei einem
// Ladefehler automatisch aufs Original zurück (fehlende Thumbnails
// dürfen NIE ein leeres Bild erzeugen). Schlägt auch das Original fehl,
// greift der `onError`-Handler des Aufrufers (bestehendes Fallback-UI).
// Externe URLs (z. B. Flashscore) laufen unverändert durch.
// =====================================================================

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  /** Original-Bild-URL (absolut oder relativ). */
  src: string;
  /** Thumbnail-Größe (100/200/400) passend zur Render-Größe der Karte. */
  thumbSize: ApiThumbnailSize;
};

const ThumbnailImage: React.FC<Props> = ({ src, thumbSize, onError, alt, ...rest }) => {
  const thumb = toApiThumbnailUrl(src, thumbSize) ?? src;
  // Merken, WELCHES Thumbnail fehlgeschlagen ist: ändert sich `src`,
  // ändert sich auch `thumb` und der Versuch startet automatisch neu.
  const [failedThumb, setFailedThumb] = useState<string | null>(null);
  const useOriginal = thumb === src || failedThumb === thumb;

  const handleError: React.ReactEventHandler<HTMLImageElement> = (event) => {
    if (!useOriginal) {
      setFailedThumb(thumb); // Thumbnail fehlt → Original versuchen
      return;
    }
    onError?.(event); // Original kaputt → Fallback-UI des Aufrufers
  };

  // eslint-disable-next-line jsx-a11y/alt-text
  return <img src={useOriginal ? src : thumb} alt={alt ?? ''} onError={handleError} {...rest} />;
};

export default ThumbnailImage;
